<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settings.settingsLabel') }}</span>
        <h3 class="page-title">{{ $t('settings.country.editCountry') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/ustawienia/kraje')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-button theme="success" @click.prevent="validateForm">{{ $t('ui.saveChanges') }}</d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form :class="{ 'is-loading': !allDataLoaded }">
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="country-edit-form" tag="form" v-if="loaded">
              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('settings.country.countryName') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('settings.country.countryInvoiceCode') }}:
                    </label>

                    <validation-provider
                      rules="required|only-big-letters|min:2|max:3"
                      name="code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('settings.country.countryAddressCode') }}:
                    </label>

                    <validation-provider
                      rules="required|only-big-letters|min:2|max:3"
                      name="address_code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="address_code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="3">
                  <div class="form-group pt-4">
                    <label>
                      <d-checkbox
                        inline
                        toggle-small
                        v-model="is_eu"
                        toggle>
                        {{ $t('settings.country.isEU') }}
                      </d-checkbox>
                    </label>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group pt-4">
                    <label>
                      <d-checkbox
                        inline
                        toggle-small
                        v-model="verify_tax_number"
                        toggle>
                        {{ $t('settings.country.verifyTaxNumber') }}
                      </d-checkbox>
                    </label>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.language') }}:
                    </label>

                    <validation-provider
                      rules="required|non_zero"
                      name="language-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="default_language_id"
                        :state="errors.length ? 'invalid' : null"
                        :options="languagesOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('settings.currency.currency') }}:
                    </label>

                    <validation-provider
                      rules="required|non_zero"
                      name="currency-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="default_currency_id"
                        :state="errors.length ? 'invalid' : null"
                        :options="currenciesOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
            </validation-observer>

            <div v-if="!loaded" class="pb-3 pt-3 text-center">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>

          <d-tab :title="$t('settings.country.translations')">
            <h4 class="pb-3 mb-0 border-bottom d-flex">
              {{ $t('settings.transaltion.translations') }}

              <d-button
                class="ml-auto"
                theme="success"
                @click.prevent="addTranslation">
                <i class="material-icons">add</i>
                {{ $t('settings.transaltion.addTranslation') }}
              </d-button>
            </h4>

            <table class="table mt-2 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                  <th scope="col" class="border-0" style="width: calc(100% - 350px);">{{ $t('settings.country.countryName') }}</th>
                  <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                </tr>
              </thead>
              <tbody v-if="country_translations.length">
                <tr
                  v-for="(translation, index) of country_translations"
                  :key="'price-' + index">
                  <td class="align-middle">
                    {{ getLanguageByID(translation.language_id) }}
                  </td>
                  <td class="align-middle">
                    {{ translation.name }}
                  </td>
                  <td class="align-middle">
                    <d-button
                      theme="primary"
                      @click.prevent="editTranslation(translation)"
                      class="mr-2"
                      :title="$t('ui.edit')">
                      <i class="material-icons">edit</i>
                    </d-button>
                    <d-button
                      theme="danger"
                      :disabled="removeTranslationInProgress"
                      @click.prevent="removeTranslation(translation.id)"
                      :title="$t('ui.delete')">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="3" class="text-center">
                    {{ $t('settings.country.noTranslationsForThisCountry') }}&hellip;
                  </td>
                </tr>
              </tbody>
            </table>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>


    <country-translation-new-popup
      v-if="showAddTranslationPopup"
      :countryID="this.id"
      :languages="languages" />

    <country-translation-edit-popup
      v-if="showEditTranslationPopup"
      :countryID="this.id"
      :translationData="selectedTranslation"
      :languages="languages" />
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import CommonGetters from './../../utils/CommonGetters.js';
import CountryTranslationNewPopup from './CountryTranslationNewPopup';
import CountryTranslationEditPopup from './CountryTranslationEditPopup';

export default {
  name: 'country-edit',
  components: {
    'country-translation-new-popup': CountryTranslationNewPopup,
    'country-translation-edit-popup': CountryTranslationEditPopup
  },
  computed: {
    allDataLoaded () {
      return this.loadedLanguages && this.loadedCurrencies;
    },
    languagesOptions () {
      return [{ value: 0, text: this.$t('settings.language.chooseLanguage') }].concat(this.languages.map(language => ({
        value: language.id,
        text: language.name
      })));
    },
    currenciesOptions () {
      return [{ value: 0, text: this.$t('shipments.popups.selectCurrency') }].concat(CommonGetters.getCurrenciesForSelect(this.currencies));
    }
  },
  data() {
    return {
      id: 0,
      name: '',
      code: '',
      address_code: '',
      is_eu: false,
      default_language_id: null,
      default_currency_id: null,
      verify_tax_number: false,
      loaded: false,
      loadError: false,
      languages: [],
      currencies: [],
      loadedLanguages: false,
      loadedCurrencies: false,
      country_translations: [],
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      selectedTranslation: 0,
      removeTranslationInProgress: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: '/countries/item/',
      instanceFields: ['name', 'code', 'address_code', 'is_eu', 'verify_tax_number', 'default_language_id', 'default_currency_id', 'country_translations'],
      loadedKey: 'loaded',
      booleanFields: ['is_eu', 'verify_tax_number'],
      params: {
        with: ['countryTranslations']
      }
    });

    this.loadLanguages();
    this.loadCurrencies();
    this.$bus.$on('country-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('country-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('country-reload-translations', this.loadTranslations);
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['country-edit-form'], this.submitForm);
    },
    submitForm () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/countries/update/' + this.id,
        formData: {
          name: this.name,
          code: this.code,
          address_code: this.address_code,
          is_eu: this.is_eu,
          verify_tax_number: +this.verify_tax_number,
          default_currency_id: this.default_currency_id,
          default_language_id: this.default_language_id
        },
        successTitle: this.$t('settings.country.countryEditSuccessTitle'),
        successText: this.$t('settings.country.countryEditSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.country.countryEditErrorText'),
        successAction: () => {
          this.$router.push('/ustawienia/kraje')
        }
      });
    },
    loadLanguages () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    loadCurrencies () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError',
      });
    },
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/countries/item/',
        instanceFields: ['country_translations'],
        loadedKey: null,
        params: {
          with: ['countryTranslations']
        }
      });
    },
    addTranslation () {
      this.showAddTranslationPopup = true;
    },
    editTranslation (translationData) {
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (translationID) {
      this.removeTranslationInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/countries/translations/delete/',
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('country-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('country-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('country-reload-translations', this.loadTranslations);
  }
};
</script>

<style lang="scss" scoped>
  form.is-loading {
    opacity: .25;
    pointer-events: none;
  }
  </style>
